// src/components/Dashboard.js - Project management dashboard
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { generateClient } from 'aws-amplify/api';
import { listProjects } from '../graphql/queries';

const client = generateClient();

function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Progress');
  const [serviceFilter, setServiceFilter] = useState('All Services');
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    filterProjects();
  }, [projects, searchTerm, statusFilter, serviceFilter]);

  const filterProjects = () => {
    let filtered = projects;

    if (searchTerm) {
      filtered = filtered.filter(project =>
        project.projectName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'All Progress') {
      filtered = filtered.filter(project => project.status === statusFilter);
    }

    if (serviceFilter !== 'All Services') {
      filtered = filtered.filter(project => project.serviceType === serviceFilter);
    }

    setFilteredProjects(filtered);
  };

  const fetchProjects = async () => {
    try {
      console.log('=== FETCHING PROJECTS ===');
      console.log('User from context:', user);
      
      const result = await client.graphql({
        query: listProjects
      });
      
      console.log('Raw GraphQL result:', result);
      console.log('Projects data:', result.data.listProjects.items);
      console.log('Number of projects:', result.data.listProjects.items.length);
      
      const projectsData = result.data.listProjects.items;
      setProjects(projectsData);
    } catch (error) {
      console.error('Failed to fetch projects - Full error:', error);
      setProjects([]);
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      'Completed': 'bg-green-100 text-green-800',
      'In Progress': 'bg-blue-100 text-blue-800',
      'Client Review': 'bg-yellow-100 text-yellow-800',
      'Not Started': 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6 font-sans text-gray-800 relative">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="absolute right-6 top-6 flex gap-2">
          <button 
            onClick={() => navigate('/new-request')}
            className="bg-primary hover:bg-blue-600 text-white font-semibold px-4 py-2 rounded shadow"
          >
            New Request
          </button>
          <button 
            onClick={() => navigate('/support')}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold px-4 py-2 rounded shadow"
          >
            Need support?
          </button>
          <button 
            onClick={logout}
            className="bg-red-500 hover:bg-red-600 text-white font-semibold px-4 py-2 rounded shadow"
          >
            Logout
          </button>
        </div>
      </div>
      
      {/* Logo */}
      <div className="flex justify-center mt-10">
        <img 
          src="https://compasslanguages.s3.us-east-2.amazonaws.com/logos/Bausch_Health_Logo.png" 
          alt="Bausch Health Logo" 
          className="h-20 object-contain" 
        />
      </div>

      <h1 className="text-3xl font-bold text-primary text-center">Project Management Dashboard</h1>

      {/* Dashboard Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-primary">
          <div className="text-2xl font-bold text-gray-800">{projects.length}</div>
          <div className="text-gray-600">Total Projects</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
          <div className="text-2xl font-bold text-gray-800">
            {projects.filter(p => p.status === 'Completed').length}
          </div>
          <div className="text-gray-600">Completed</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
          <div className="text-2xl font-bold text-gray-800">
            {projects.filter(p => p.status === 'In Progress').length}
          </div>
          <div className="text-gray-600">In Progress</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-yellow-500">
          <div className="text-2xl font-bold text-gray-800">
            {projects.filter(p => p.status === 'Client Review').length}
          </div>
          <div className="text-gray-600">Pending Review</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4 items-center">
          <div className="flex-1">
            <input 
              type="text" 
              placeholder="Search projects..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-primary" 
            />
          </div>
          <div className="flex gap-2">
            <select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 p-2 rounded"
            >
              <option>All Progress</option>
              <option>Completed</option>
              <option>In Progress</option>
              <option>Client Review</option>
              <option>Not Started</option>
            </select>
            <select 
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              className="border border-gray-300 p-2 rounded"
            >
              <option>All Services</option>
              <option>Translation</option>
              <option>Localization</option>
              <option>Subtitling</option>
            </select>
          </div>
        </div>
      </div>

      {/* Projects Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gray-100 border-b-2 border-gray-300 sticky top-0">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300">Project Name</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300">Source Language</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300">Target Language(s)</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300">Due Date</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300">Service Type</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300">Progress</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {filteredProjects.map((project) => (
                  <tr key={project.id} className="hover:bg-gray-50 border-b border-gray-200">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">
                      {project.projectName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border-r border-gray-200">
                      {project.sourceLanguage}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border-r border-gray-200">
                      {project.targetLanguages}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border-r border-gray-200">
                      {project.dueDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border-r border-gray-200">
                      {project.serviceType}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap border-r border-gray-200">
                      {getStatusBadge(project.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button className="text-primary hover:text-blue-600 font-medium mr-3">View</button>
                      <button className="text-gray-500 hover:text-gray-700 font-medium">Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="pt-8 text-center text-sm text-gray-600">
        Modular Customized Localization Platform by Compass Languages
        <div className="mt-2 flex justify-center">
          <img 
            src="https://compasslanguages.s3.us-east-2.amazonaws.com/logos/CompassLanguages_Logo4_PNG.png" 
            className="h-20" 
            alt="Compass Logo" 
          />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;